import { socialApiClient } from '@/shared/api-client/social-api-client';
import {
  type AddCreatorRequestDTO,
  type CreatorsInListDTO,
  type CreatorsListRequestDTO,
  type RemoveCreatorRequestDTO,
} from '../dto';
import {
  addCreatorsSchema,
  creatorsListRequestSchema,
  removeCreatorsSchema,
} from '../saved-creators-list-schema';
import type { SavedList } from '../saved-creators-list-types';
import type { SOURCE_PLATFORM_TYPE } from '../../public-filter';

export const FetchSavedListLists = async (
  workspace_id: string,
  signal?: AbortSignal,
): Promise<SavedList[]> => {
  const response = await socialApiClient.get('creators/saved-lists', {
    searchParams: {
      workspace_id,
    },
    signal,
  });
  const data = await response.json<{
    lists: SavedList[];
  }>();
  return data.lists;
};

export const CreateSavedList = async (
  params: {
    workspace_id: string;
    title: string;
  },
  signal?: AbortSignal,
): Promise<SavedList> => {
  const response = await socialApiClient.post('creators/saved-lists', {
    json: params,
    signal,
  });
  return await response.json();
};

export const DeleteSavedList = async (listId: string, signal?: AbortSignal): Promise<void> => {
  await socialApiClient.delete(`creators/saved-lists/${listId}`, { signal });
};

export const AddCreatorsToSavedList = async (
  listId: string,
  request: AddCreatorRequestDTO,
  signal?: AbortSignal,
): Promise<void> => {
  const parsedRequest = addCreatorsSchema.parse(request);
  await socialApiClient.post(`creators/saved-lists/${listId}/creators`, {
    json: parsedRequest,
    signal,
  });
};

export const RemoveCreatorsFromSavedList = async (
  listId: string,
  request: RemoveCreatorRequestDTO,
  signal?: AbortSignal,
): Promise<void> => {
  const parsedRequest = removeCreatorsSchema.parse(request);
  await socialApiClient.patch(`creators/saved-lists/${listId}/creators`, {
    json: parsedRequest,
    signal,
  });
};

export const FetchSavedCreatorsList = async (
  listId: string,
  signal?: AbortSignal,
): Promise<SavedList> => {
  const response = await socialApiClient.get(`creators/saved-lists/${listId}`, {
    signal,
  });
  return await response.json();
};

export const UpdateSavedListTitle = async (
  listId: string,
  title: string,
  signal?: AbortSignal,
): Promise<SavedList> => {
  const response = await socialApiClient.patch(`creators/saved-lists/${listId}/title`, {
    json: { title },
    signal,
  });
  return await response.json();
};

export const FetchCreatorsInSavedList = async (
  listId: string,
  params: CreatorsListRequestDTO | null,
  signal?: AbortSignal,
): Promise<{
  creators: Array<CreatorsInListDTO>;
  page: number;
  total_pages: number;
  total_count: number;
}> => {
  const parsedRequest = creatorsListRequestSchema.parse(params);
  const response = await socialApiClient.get(`creators/saved-lists/${listId}/creators`, {
    searchParams: parsedRequest || undefined,
    signal,
  });
  return await response.json();
};

type FetchHandleListFromSavedListResponse = {
  creators: {
    username: string;
    platform?: SOURCE_PLATFORM_TYPE;
  }[];
};

export const FetchHandleListFromSavedList = async (
  listId: string,
  signal?: AbortSignal,
): Promise<FetchHandleListFromSavedListResponse> => {
  const res = await socialApiClient.get(`creators/saved-lists/${listId}/creator-handles`, {
    signal,
  });
  return await res.json();
};
